/**
 * 通知ドロワー (リデザイン) の表示整形 — notification-drawer-redesign (2026-09-06)。
 *
 * 1 行目「{依頼者名}  {書類名 / 本文}  {時刻}」、日付見出し「2026年9月6日」に使う pure 関数。
 * 既存の `notificationFormat.ts` (件数付きタイトル文 / 発生時間帯 / プレビュー重複抑止) はそのまま残し、
 * 新着ポップアップは従来どおりそちらを使う。ここは **ドロワーだけ** が使う。
 *
 * ## 契約
 * - **title の文字列解析をしない。** 判定に使うのは type / resourceName / count / mixedResource /
 *   message / messagePreview の構造化 field だけ (SPEC §7.1.3.2 と同じ不変条件)
 * - 書類系で `resourceName` が無い (legacy / 集約対象外の writer) ときは **元の title 文へ fallback**
 * - timezone は Asia/Tokyo 固定。不正値は空文字 (Invalid Date を出さない)
 */
import type { Notification } from '@/types';
import type { Locale } from '@/i18n';
import { formatMonthDayEn } from '@/i18n/format';
import { notificationCategory } from '@/utils/notificationCategory';
import {
  formatGroupedNotificationTitle,
  notificationGroupCount,
  renderNotificationText,
  resolveNotificationPreviewLine,
} from '@/utils/notificationFormat';

const TZ = 'Asia/Tokyo';

function trimmed(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * ドロワーで「この通知の時刻」として使う ISO 文字列。
 * 集約された通知は最後に起きた時刻 (`lastAt`)、それ以外は `createdAt`。どちらも不正なら空文字。
 */
export function notificationDisplayAt(
  n: Pick<Notification, 'createdAt'> & { lastAt?: string | null; count?: number | null },
): string {
  if (notificationGroupCount(n) > 1 && toDate(n.lastAt)) return n.lastAt as string;
  return toDate(n.createdAt) ? n.createdAt : '';
}

/** `HH:mm` (JST、時も 0 埋め)。日付見出しの下に並ぶ行の時刻。 */
export function formatNotificationClockTime(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('ja-JP', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * 日付見出し `2026年9月6日` (JST)。英語表示は「Sep 6」(JST の当年以外は「Sep 6, 2025」)。
 * `now` は英語表示で当年かを決めるための現在時刻 (テスト用)。
 */
export function formatNotificationDateHeading(
  value: string | null | undefined,
  locale: Locale = 'ja',
  now: Date = new Date(),
): string {
  const d = toDate(value);
  if (!d) return '';
  if (locale !== 'ja') return formatMonthDayEn(d, { now }) ?? '';
  return d.toLocaleDateString('ja-JP', {
    timeZone: TZ,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** 日付ごとのまとまりに使うキー `YYYY-MM-DD` (JST)。不正値は空文字 (= 「日付不明」の束)。 */
export function notificationDateKey(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return parts; // en-CA は YYYY-MM-DD
}

/**
 * 1 行目に出す主文。
 *
 * | 種類 | 主文 |
 * |------|------|
 * | 書類提出 / 書類確認 | `resourceName` (集約なら `銀行口座資料 3件`、混在なら `書類 3件`)。無ければ元 title |
 * | メッセージ | 本文プレビュー (`resolveNotificationPreviewLine`) → `message` → 元 title |
 * | それ以外 | 件数付きタイトル文 (`formatGroupedNotificationTitle`) → 元 title |
 */
export function formatNotificationPrimaryLine(n: Notification, locale: Locale = 'ja'): string {
  const fallback = formatGroupedNotificationTitle(n, locale) || trimmed(n.title);
  const category = notificationCategory(n.type);

  if (category === 'document' || category === 'review') {
    const count = notificationGroupCount(n);
    const name = trimmed(n.resourceName);
    if (count > 1) {
      return n.mixedResource === true || !name
        ? renderNotificationText({ key: 'drawer.documentsCount', params: { count } }, locale)
        : renderNotificationText({ key: 'drawer.namedCount', params: { name, count } }, locale);
    }
    return name || fallback;
  }

  if (category === 'message') {
    return trimmed(n.message) || resolveNotificationPreviewLine(n) || fallback;
  }

  return fallback;
}

/**
 * 3 行目以降の補足行 (主文と 2 行目のパンくずに収まらない情報)。steering 525 の契約を保つ:
 *
 * - 書類提出 (`document`): `message` は「コレクション「…」に書類が…」の定型文で主文と重複するため出さない
 * - メッセージ (`message`): 主文が `message` なので、`messagePreview` が **重複でない** ときだけ出す
 *   (`resolveNotificationPreviewLine` が null = 先頭 N 字の複製 → 出さない)
 * - それ以外 (書類確認 / 案内 / メール / 案件 / タスク / その他): `message` (誰が何をしたか・送信先) を残し、
 *   `messagePreview` が別内容 (差し戻し理由など) なら続けて出す
 * - 文字列は切り詰めない (省略は呼び出し側の `truncate` が担う)。主文と同じ文は出さない
 */
export function notificationSupplementLines(n: Notification, locale: Locale = 'ja'): string[] {
  const category = notificationCategory(n.type);
  const primary = formatNotificationPrimaryLine(n, locale);
  const lines: string[] = [];
  const push = (value: string | null | undefined) => {
    const v = typeof value === 'string' ? value : '';
    if (!v.trim() || v === primary || lines.includes(v)) return;
    lines.push(v);
  };
  if (category !== 'document' && category !== 'message') push(n.message);
  push(resolveNotificationPreviewLine(n));
  return lines;
}

export interface NotificationDateSection {
  /** `YYYY-MM-DD` (JST)。日付不明は空文字 */
  key: string;
  /** `2026年9月6日`。日付不明は「日付不明」 */
  heading: string;
  items: Notification[];
}

/**
 * 受け取った順 (API の並び = 未読→既読 / 新しい順) を保ったまま、日付が変わるところで束ねる。
 * 束ねるキーは表示言語に依らない (JST の `YYYY-MM-DD`)。見出しだけを表示言語で出す。
 * 並べ替えはしない (API の順序を正とする)。
 */
export function groupNotificationsByDate(
  items: Notification[],
  locale: Locale = 'ja',
): NotificationDateSection[] {
  const unknownDate = renderNotificationText({ key: 'drawer.unknownDate', params: {} }, locale);
  const sections: NotificationDateSection[] = [];
  for (const n of items) {
    const at = notificationDisplayAt(n);
    const key = notificationDateKey(at);
    const last = sections[sections.length - 1];
    if (last && last.key === key) {
      last.items.push(n);
      continue;
    }
    sections.push({
      key,
      heading: formatNotificationDateHeading(at, locale) || unknownDate,
      items: [n],
    });
  }
  return sections;
}
